import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, Clock, User, Store, DollarSign, Send, 
  TicketCheck, Banknote, PackageCheck, AlertTriangle, Eye,
  Loader2, History, MessageSquare, Save, ChevronDown, ChevronUp,
  RotateCcw, RefreshCw, CalendarDays, MapPin, Building2, Phone, Bell, X,
  XCircle, AlertOctagon, Undo2, HandCoins
} from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const AdminTransactionTimeline = ({ transaction: initialTransaction, token, onUpdate }) => {
  const [transaction, setTransaction] = useState(initialTransaction);
  const [loading, setLoading] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [adminNotes, setAdminNotes] = useState(initialTransaction?.admin_notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(initialTransaction?.history || []);

  // SMS reminder modal state
  const [smsModal, setSmsModal] = useState({ open: false, party: null, step: null, stepLabel: '' });
  const [smsMessage, setSmsMessage] = useState('');
  const [smsPhone, setSmsPhone] = useState('');
  const [smsRecipientEmail, setSmsRecipientEmail] = useState('');
  const [smsTemplateLoading, setSmsTemplateLoading] = useState(false);
  const [smsSending, setSmsSending] = useState(false);

  // Cancel transfer modal state
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonPreset, setCancelReasonPreset] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [evidenceFiles, setEvidenceFiles] = useState([]); // [{url, filename, uploading, error}]
  const [uploadingCount, setUploadingCount] = useState(0);

  // Predefined cancellation reasons
  const CANCEL_REASON_PRESETS = [
    { value: 'delai_12h', label: 'Délai de 12h dépassé sans confirmation' },
    { value: 'demande_acheteur', label: "Demande de l'acheteur" },
    { value: 'demande_vendeur', label: 'Demande du vendeur' },
    { value: 'fraude_detectee', label: 'Fraude détectée / activité suspecte' },
    { value: 'billets_non_conformes', label: 'Billets invalides ou non conformes' },
    { value: 'paiement_incorrect', label: 'Paiement incorrect (montant erroné)' },
    { value: 'impossibilite_finaliser', label: 'Impossibilité de finaliser le transfert' },
    { value: 'evenement_annule', label: "L'événement a été annulé ou reporté" },
    { value: 'autre', label: 'Autre (préciser)' },
  ];

  // Live chrono — updates every 30 sec
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const elapsed = (() => {
    const created = new Date(transaction.created_at || Date.now()).getTime();
    const diffMs = Math.max(0, nowTs - created);
    const totalMin = Math.floor(diffMs / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    const over12h = h >= 12 && !transaction.cancelled && transaction.status !== 'completed';
    return { h, m, over12h, days: Math.floor(h / 24) };
  })();

  useEffect(() => {
    setTransaction(initialTransaction);
    setAdminNotes(initialTransaction?.admin_notes || '');
    setHistory(initialTransaction?.history || []);
  }, [initialTransaction]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get completion info for a step
  const getStepCompletionInfo = (stepKey) => {
    // Map frontend step keys to actual DB field names
    const fieldMap = {
      'buyer_confirmed': { 
        fields: ['buyer_confirmed_at'], 
        byFields: ['buyer_confirmed_by_admin', 'buyer_confirmed_by'] 
      },
      'seller_confirmed': { 
        fields: ['seller_confirmed_at'], 
        byFields: ['seller_confirmed_by_admin', 'seller_confirmed_by'] 
      },
      'payment_completed': { 
        fields: ['payment_completed_at'], 
        byFields: ['payment_confirmed_by', 'payment_completed_by'] 
      },
      'tickets_verified': { 
        fields: ['tickets_verified_at'], 
        byFields: ['tickets_verified_by'] 
      },
      'funds_released': { 
        fields: ['funds_released_at', 'payment_sent_to_seller_at'], 
        byFields: ['funds_released_by', 'payment_sent_to_seller_by'] 
      },
      'tickets_transferred': { 
        fields: ['tickets_transferred_to_buyer_at', 'tickets_transferred_at'], 
        byFields: ['tickets_transferred_to_buyer_by', 'tickets_transferred_by'] 
      },
      'completed': { 
        fields: ['completed_at'], 
        byFields: ['completed_by'] 
      }
    };
    
    const mapping = fieldMap[stepKey];
    if (!mapping) return null;
    
    // Try each possible field name
    let date = null;
    let by = null;
    
    for (const field of mapping.fields) {
      if (transaction[field]) {
        date = transaction[field];
        break;
      }
    }
    
    for (const field of mapping.byFields) {
      if (transaction[field]) {
        by = transaction[field];
        break;
      }
    }
    
    if (date || by) {
      return {
        date: date ? formatDate(date) : null,
        by: by || null
      };
    }
    return null;
  };

  // Refresh transaction data
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await axios.get(`${API}/transactions/${transaction.id}`);
      const updatedTransaction = response.data;
      setTransaction(updatedTransaction);
      setAdminNotes(updatedTransaction.admin_notes || '');
      setHistory(updatedTransaction.history || []);
      toast.success('Transaction actualisée');
      if (onUpdate) onUpdate(updatedTransaction);
    } catch (error) {
      toast.error('Erreur lors de l\'actualisation');
    } finally {
      setRefreshing(false);
    }
  };

  // Define all timeline steps with admin actions (always available)
  const _timelineStepsAll = [
    {
      key: 'created',
      label: 'Demande créée',
      icon: CheckCircle,
      description: `Créée le ${formatDate(transaction.created_at)}`,
      isCompleted: () => true,
      statusField: null,
      actions: []
    },
    {
      key: 'buyer_confirmed',
      label: 'Confirmation acheteur',
      icon: User,
      description: `Acheteur: ${transaction.buyer_email}`,
      isCompleted: () => transaction.buyer_confirmed,
      statusField: 'buyer_confirmed',
      actions: [
        { label: 'Confirmer', action: 'force_buyer_confirm', variant: 'success', showWhen: 'not_completed' },
        { label: 'Annuler', action: 'unconfirm_buyer', variant: 'destructive', showWhen: 'completed' }
      ],
      smsReminder: { party: 'buyer', step: 'confirm', label: "Rappeler à l'acheteur de confirmer" }
    },
    {
      key: 'seller_confirmed',
      label: 'Confirmation vendeur',
      icon: Store,
      description: `Vendeur: ${transaction.seller_email}`,
      isCompleted: () => transaction.seller_confirmed,
      statusField: 'seller_confirmed',
      actions: [
        { label: 'Confirmer', action: 'force_seller_confirm', variant: 'success', showWhen: 'not_completed' },
        { label: 'Annuler', action: 'unconfirm_seller', variant: 'destructive', showWhen: 'completed' }
      ],
      smsReminder: { party: 'seller', step: 'confirm', label: 'Rappeler au vendeur de confirmer' }
    },
    {
      key: 'payment_completed',
      label: 'Paiement acheteur reçu',
      icon: DollarSign,
      description: `Montant: ${transaction.calculated_fees?.buyer_pays || transaction.price}$`,
      isCompleted: () => {
        // Check multiple indicators
        if (transaction.payment_completed) return true;
        if (transaction.payment_completed_at) return true;
        if (transaction.tickets_verified) return true;
        if (transaction.funds_released) return true;
        const paidStatuses = ['payment_completed', 'payment_received', 'tickets_uploaded', 'tickets_verified', 'admin_verified', 'funds_released', 'tickets_transferred', 'completed'];
        return paidStatuses.includes(transaction.status);
      },
      statusField: 'payment_completed',
      actions: [
        { label: 'Confirmer paiement', action: 'confirm_payment', variant: 'success', showWhen: 'not_completed' },
        { label: 'Annuler paiement', action: 'unconfirm_payment', variant: 'destructive', showWhen: 'completed' }
      ],
      smsReminder: { party: 'buyer', step: 'payment', label: "Rappeler à l'acheteur d'envoyer son paiement" },
      rejectAction: {
        label: 'Montant incorrect',
        prefillReason: `Montant reçu incorrect (attendu: ${transaction.total_amount || transaction.price}$). Précisez le montant reçu ou le problème.`
      }
    },
    {
      key: 'tickets_verified',
      label: 'Billets vérifiés',
      icon: Eye,
      description: 'Vérification des billets par l\'admin',
      isCompleted: () => transaction.tickets_verified,
      statusField: 'tickets_verified',
      actions: [
        { label: 'Vérifier billets', action: 'verify_tickets', variant: 'success', showWhen: 'not_completed' },
        { label: 'Annuler vérification', action: 'unverify_tickets', variant: 'destructive', showWhen: 'completed' }
      ],
      smsReminder: { party: 'seller', step: 'tickets_send', label: "Rappeler au vendeur d'envoyer ses billets" },
      rejectAction: {
        label: 'Billets non conformes',
        prefillReason: 'Les billets reçus ne correspondent pas à ceux entendus (section/rangée/siège différents ou billets invalides). Précisez le problème.'
      }
    },
    {
      key: 'funds_released',
      label: 'Paiement envoyé au vendeur',
      icon: Banknote,
      description: `Vendeur: ${transaction.seller_email}`,
      isCompleted: () => transaction.funds_released,
      statusField: 'funds_released',
      actions: [
        { label: 'Envoyer paiement', action: 'send_payment', variant: 'success', showWhen: 'not_completed' },
        { label: 'Annuler envoi', action: 'unsend_payment', variant: 'destructive', showWhen: 'completed' }
      ],
      smsReminder: { party: 'seller', step: 'interac_setup', label: "Rappeler au vendeur de configurer son Interac" }
    },
    {
      key: 'tickets_transferred',
      label: 'Billets transférés à l\'acheteur',
      icon: TicketCheck,
      description: `Acheteur: ${transaction.buyer_email}`,
      isCompleted: () => transaction.tickets_transferred_to_buyer,
      statusField: 'tickets_transferred_to_buyer',
      actions: [
        { label: 'Transférer billets', action: 'transfer_tickets', variant: 'success', showWhen: 'not_completed' },
        { label: 'Annuler transfert', action: 'untransfer_tickets', variant: 'destructive', showWhen: 'completed' }
      ]
    },
    {
      key: 'completed',
      label: 'Transaction complétée',
      icon: PackageCheck,
      description: transaction.status === 'completed' ? 'Transaction terminée' : 'En attente de finalisation',
      isCompleted: () => transaction.status === 'completed',
      statusField: null,
      actions: [
        { label: 'Marquer complété', action: 'mark_completed', variant: 'success', showWhen: 'not_completed' },
        { label: 'Réouvrir', action: 'reopen_transaction', variant: 'warning', showWhen: 'completed' }
      ]
    }
  ];

  // Post-cancellation refund/return step overrides
  const buildRefundReturnSteps = () => {
    const steps = [];
    if (transaction.refund_required) {
      steps.push({
        key: 'refund_step',
        label: 'Remboursement acheteur',
        icon: HandCoins,
        description: `Retour du paiement à ${transaction.buyer_email}`,
        isCompleted: () => !!transaction.refund_completed,
        statusField: null,
        actions: [
          !transaction.refund_in_progress
            ? { label: 'Marquer remboursement en cours', action: 'mark_refund_in_progress', variant: 'warning', showWhen: 'always' }
            : !transaction.refund_completed
              ? { label: 'Confirmer remboursement effectué', action: 'mark_refund_completed', variant: 'success', showWhen: 'always' }
              : null,
        ].filter(Boolean),
      });
    }
    if (transaction.tickets_return_required) {
      steps.push({
        key: 'tickets_return_step',
        label: 'Retour billets au vendeur',
        icon: Undo2,
        description: `Retour des billets à ${transaction.seller_email}`,
        isCompleted: () => !!transaction.tickets_return_completed,
        statusField: null,
        actions: [
          !transaction.tickets_return_in_progress
            ? { label: 'Marquer retour en cours', action: 'mark_tickets_return_in_progress', variant: 'warning', showWhen: 'always' }
            : !transaction.tickets_return_completed
              ? { label: 'Confirmer retour effectué', action: 'mark_tickets_return_completed', variant: 'success', showWhen: 'always' }
              : null,
        ].filter(Boolean),
      });
    }
    return steps;
  };

  // Final timeline: replace the last "outgoing" steps with refund/return when cancelled
  const timelineSteps = transaction.cancelled
    ? [
        ..._timelineStepsAll.filter((s) =>
          ['created', 'buyer_confirmed', 'seller_confirmed', 'payment_completed', 'tickets_verified'].includes(s.key)
        ),
        {
          key: 'cancelled',
          label: 'Transaction annulée',
          icon: XCircle,
          description: `Annulée par ${transaction.cancelled_by || 'admin'} — ${transaction.cancellation_reason || 'raison non spécifiée'}`,
          isCompleted: () => true,
          statusField: null,
          actions: [],
        },
        ...buildRefundReturnSteps(),
      ]
    : _timelineStepsAll;

  // ============ SMS Reminder handlers ============
  const openSmsReminderModal = async (party, step, stepLabel) => {
    setSmsModal({ open: true, party, step, stepLabel });
    setSmsMessage('');
    setSmsPhone('');
    setSmsRecipientEmail('');
    setSmsTemplateLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/transactions/${transaction.id}/reminder-template`,
        { params: { party, step } }
      );
      setSmsMessage(res.data.template || '');
      setSmsPhone(res.data.phone || '');
      setSmsRecipientEmail(res.data.recipient_email || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur chargement du modèle');
    } finally {
      setSmsTemplateLoading(false);
    }
  };

  const closeSmsModal = () => {
    if (smsSending) return;
    setSmsModal({ open: false, party: null, step: null, stepLabel: '' });
    setSmsMessage('');
  };

  const sendSmsReminder = async () => {
    if (!smsMessage.trim()) {
      toast.error('Le message ne peut pas être vide');
      return;
    }
    if (!smsPhone) {
      toast.error('Aucun numéro de téléphone enregistré pour ce destinataire');
      return;
    }
    setSmsSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/transactions/${transaction.id}/send-reminder-sms`,
        { party: smsModal.party, step: smsModal.step, message: smsMessage.trim() }
      );
      toast.success(res.data.message || 'Rappel envoyé');
      closeSmsModal();
      // Refresh to pick up history entry
      handleRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi du SMS");
    } finally {
      setSmsSending(false);
    }
  };

  // ============ CANCEL TRANSFER handler ============
  const openCancelModal = (prefillReason = '') => {
    setCancelReason(prefillReason);
    setCancelReasonPreset(prefillReason ? 'autre' : '');
    setEvidenceFiles([]);
    setCancelModal(true);
  };

  const closeCancelModal = () => {
    if (cancelling || uploadingCount > 0) return;
    setCancelModal(false);
    setCancelReason('');
    setCancelReasonPreset('');
    setEvidenceFiles([]);
  };

  const handlePresetChange = (value) => {
    setCancelReasonPreset(value);
    if (value === 'autre' || value === '') {
      // Keep whatever user has typed
      return;
    }
    const preset = CANCEL_REASON_PRESETS.find((p) => p.value === value);
    if (preset) {
      setCancelReason(preset.label);
    }
  };

  const handleEvidenceUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    event.target.value = ''; // allow re-upload of same file
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name}: format non supporté (image seulement)`);
        continue;
      }
      if (file.size > 8 * 1024 * 1024) {
        toast.error(`${file.name}: fichier trop volumineux (max 8 MB)`);
        continue;
      }
      const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      setEvidenceFiles((prev) => [...prev, { tempId, filename: file.name, uploading: true }]);
      setUploadingCount((c) => c + 1);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(
          `${API}/admin/transactions/upload-evidence`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setEvidenceFiles((prev) =>
          prev.map((f) =>
            f.tempId === tempId
              ? { ...f, uploading: false, url: res.data.url, filename: res.data.filename }
              : f
          )
        );
      } catch (err) {
        toast.error(err.response?.data?.detail || `Erreur upload ${file.name}`);
        setEvidenceFiles((prev) => prev.filter((f) => f.tempId !== tempId));
      } finally {
        setUploadingCount((c) => c - 1);
      }
    }
  };

  const removeEvidence = (tempId) => {
    setEvidenceFiles((prev) => prev.filter((f) => f.tempId !== tempId));
  };

  const submitCancel = async () => {
    if (!cancelReason.trim() || cancelReason.trim().length < 3) {
      toast.error('Veuillez fournir une raison (min. 3 caractères)');
      return;
    }
    if (uploadingCount > 0) {
      toast.error("Attendez la fin des téléversements avant de confirmer");
      return;
    }
    setCancelling(true);
    try {
      const evidence_urls = evidenceFiles.filter((f) => f.url).map((f) => f.url);
      const res = await axios.post(
        `${API}/admin/transactions/${transaction.id}/cancel-transfer`,
        { reason: cancelReason.trim(), evidence_urls }
      );
      toast.success(res.data.message || 'Transaction annulée');
      closeCancelModal();
      handleRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'annulation");
    } finally {
      setCancelling(false);
    }
  };

  const handleAction = async (action) => {
    setLoading(prev => ({ ...prev, [action]: true }));
    
    try {
      let endpoint = '';
      let method = 'POST';
      let body = {};
      
      switch (action) {
        case 'force_buyer_confirm':
          endpoint = `/admin/transactions/${transaction.id}/force-confirm`;
          body = { party: 'buyer' };
          break;
        case 'force_seller_confirm':
          endpoint = `/admin/transactions/${transaction.id}/force-confirm`;
          body = { party: 'seller' };
          break;
        case 'unconfirm_buyer':
          endpoint = `/admin/transactions/${transaction.id}/toggle-step`;
          body = { step: 'buyer_confirmed', value: false };
          break;
        case 'unconfirm_seller':
          endpoint = `/admin/transactions/${transaction.id}/toggle-step`;
          body = { step: 'seller_confirmed', value: false };
          break;
        case 'confirm_payment':
          endpoint = `/admin/transactions/${transaction.id}/confirm-interac-payment`;
          break;
        case 'unconfirm_payment':
          endpoint = `/admin/transactions/${transaction.id}/toggle-step`;
          body = { step: 'payment_completed', value: false };
          break;
        case 'verify_tickets':
          endpoint = `/admin/transactions/${transaction.id}/verify-tickets`;
          break;
        case 'unverify_tickets':
          endpoint = `/admin/transactions/${transaction.id}/toggle-step`;
          body = { step: 'tickets_verified', value: false };
          break;
        case 'send_payment':
          endpoint = `/admin/transactions/${transaction.id}/send-payment-to-seller`;
          break;
        case 'unsend_payment':
          endpoint = `/admin/transactions/${transaction.id}/toggle-step`;
          body = { step: 'funds_released', value: false };
          break;
        case 'transfer_tickets':
          endpoint = `/admin/transactions/${transaction.id}/transfer-tickets-to-buyer`;
          break;
        case 'untransfer_tickets':
          endpoint = `/admin/transactions/${transaction.id}/toggle-step`;
          body = { step: 'tickets_transferred_to_buyer', value: false };
          break;
        case 'mark_completed':
          endpoint = `/admin/transactions/${transaction.id}/complete`;
          break;
        case 'reopen_transaction':
          endpoint = `/admin/transactions/${transaction.id}/reopen`;
          break;
        case 'mark_refund_in_progress':
          endpoint = `/admin/transactions/${transaction.id}/mark-refund-in-progress`;
          break;
        case 'mark_refund_completed':
          endpoint = `/admin/transactions/${transaction.id}/mark-refund-completed`;
          break;
        case 'mark_tickets_return_in_progress':
          endpoint = `/admin/transactions/${transaction.id}/mark-tickets-return-in-progress`;
          break;
        case 'mark_tickets_return_completed':
          endpoint = `/admin/transactions/${transaction.id}/mark-tickets-return-completed`;
          break;
        default:
          throw new Error('Action inconnue');
      }
      
      const response = await axios.post(`${API}${endpoint}`, body);
      
      toast.success(response.data.message || 'Action effectuée avec succès');
      
      // Update local state immediately with the response data
      const updatedTransaction = response.data.transaction || response.data;
      if (updatedTransaction && updatedTransaction.id) {
        setTransaction(updatedTransaction);
        setHistory(updatedTransaction.history || history);
      } else {
        // Refund/return endpoints don't return full transaction — fetch fresh
        handleRefresh();
      }
      
      // Also notify parent component
      if (onUpdate) {
        onUpdate(updatedTransaction);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'action');
    } finally {
      setLoading(prev => ({ ...prev, [action]: false }));
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.post(`${API}/admin/transactions/${transaction.id}/notes`, {
        notes: adminNotes
      });
      toast.success('Notes enregistrées');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement des notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const getButtonVariant = (variant) => {
    switch (variant) {
      case 'success': return 'bg-green-600 hover:bg-green-700 text-white';
      case 'warning': return 'bg-amber-500 hover:bg-amber-600 text-white';
      case 'destructive': return 'bg-red-500 hover:bg-red-600 text-white';
      default: return 'bg-brand-600 hover:bg-brand-700 text-white';
    }
  };

  return (
    <div className="w-full py-2 space-y-4">
      {/* Transaction Header */}
      <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-base sm:text-lg text-gray-900 break-words">{transaction.event_name}</h3>
            <p className="text-xs sm:text-sm text-gray-500 break-all">Réf: {transaction.reference_number || transaction.reference || transaction.id}</p>
            <p className="text-[10px] sm:text-xs text-gray-400 break-all font-mono">ID: {transaction.id}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              size="sm"
              variant="outline"
              className="flex items-center gap-1"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Actualiser</span>
            </Button>
            {!transaction.cancelled && transaction.status !== 'completed' && (
              <Button
                onClick={() => openCancelModal('')}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-1"
                data-testid="cancel-transfer-btn"
              >
                <XCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Annuler transfert</span>
                <span className="sm:hidden">Annuler</span>
              </Button>
            )}
            <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap ${
              transaction.cancelled ? 'bg-red-100 text-red-800' :
              transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
              transaction.status === 'expired' ? 'bg-gray-100 text-gray-800' :
              transaction.status === 'disputed' ? 'bg-red-100 text-red-800' :
              'bg-blue-100 text-blue-800'
            }`} data-testid="transaction-status-badge">
              {transaction.cancelled ? 'annulé' : transaction.status}
            </span>
          </div>
        </div>

        {/* Chrono + cancellation banner */}
        <div className={`mt-3 rounded-lg p-2 sm:p-3 text-xs sm:text-sm flex flex-wrap items-center gap-2 ${
          transaction.cancelled ? 'bg-red-50 border border-red-200 text-red-900' :
          elapsed.over12h ? 'bg-amber-50 border border-amber-200 text-amber-900' :
          'bg-white border border-gray-100 text-gray-700'
        }`} data-testid="transaction-chrono">
          <Clock className={`w-4 h-4 flex-shrink-0 ${elapsed.over12h && !transaction.cancelled ? 'text-amber-600' : 'text-gray-500'}`} />
          <span className="font-semibold">
            {transaction.cancelled ? (
              <>Annulée — délai final: {elapsed.days > 0 ? `${elapsed.days}j ` : ''}{elapsed.h % 24}h{elapsed.m.toString().padStart(2, '0')}</>
            ) : (
              <>Ouverte depuis {elapsed.days > 0 ? `${elapsed.days}j ` : ''}{elapsed.h % 24}h{elapsed.m.toString().padStart(2, '0')}</>
            )}
          </span>
          {elapsed.over12h && !transaction.cancelled && (
            <span className="flex items-center gap-1 font-bold ml-auto">
              <AlertOctagon className="w-4 h-4" />
              Délai 12h dépassé
            </span>
          )}
          {transaction.cancelled && transaction.cancellation_reason && (
            <span className="w-full sm:ml-6 text-xs sm:text-sm">
              <strong>Raison:</strong> {transaction.cancellation_reason}
            </span>
          )}
        </div>

        {/* Event details row (date / time / venue / platform) */}
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs sm:text-sm text-gray-700 bg-white rounded-lg border border-gray-100 p-2 sm:p-3">
          {transaction.event_date && (
            <div className="flex items-center gap-1.5" data-testid="event-date-display">
              <CalendarDays className="w-4 h-4 text-brand-600 flex-shrink-0" />
              <span className="font-medium break-words">
                {transaction.event_date}
                {transaction.event_time ? ` • ${transaction.event_time}` : ''}
              </span>
            </div>
          )}
          {transaction.venue && (
            <div className="flex items-center gap-1.5 min-w-0" data-testid="event-venue-display">
              <MapPin className="w-4 h-4 text-brand-600 flex-shrink-0" />
              <span className="truncate">{transaction.venue}</span>
            </div>
          )}
          {(transaction.ticket_platform || transaction.custom_platform) && (
            <div className="flex items-center gap-1.5" data-testid="event-platform-display">
              <Building2 className="w-4 h-4 text-brand-600 flex-shrink-0" />
              <span>{transaction.custom_platform || transaction.ticket_platform}</span>
            </div>
          )}
          {!transaction.event_date && !transaction.venue && (
            <span className="text-xs text-gray-400 italic">Détails événement non renseignés</span>
          )}
        </div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <span className="text-gray-500 text-xs uppercase tracking-wide">Acheteur</span>
            {transaction.buyer_name && (
              <p className="font-semibold text-gray-900 break-words">{transaction.buyer_name}</p>
            )}
            <p className="font-medium text-xs sm:text-sm break-all">{transaction.buyer_email}</p>
            {transaction.buyer_client_number && (
              <p className="text-xs text-gray-400">{transaction.buyer_client_number}</p>
            )}
            {transaction.buyer_phone && (
              <a href={`tel:${transaction.buyer_phone}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                <Phone className="w-3 h-3" /> {transaction.buyer_phone}
              </a>
            )}
          </div>
          <div>
            <span className="text-gray-500 text-xs uppercase tracking-wide">Vendeur</span>
            {transaction.seller_name && (
              <p className="font-semibold text-gray-900 break-words">{transaction.seller_name}</p>
            )}
            <p className="font-medium text-xs sm:text-sm break-all">{transaction.seller_email}</p>
            {transaction.seller_client_number && (
              <p className="text-xs text-gray-400">{transaction.seller_client_number}</p>
            )}
            {transaction.seller_phone && (
              <a href={`tel:${transaction.seller_phone}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                <Phone className="w-3 h-3" /> {transaction.seller_phone}
              </a>
            )}
          </div>
          <div>
            <span className="text-gray-500 text-xs uppercase tracking-wide">Prix</span>
            <p className="font-medium">{transaction.price}$ {transaction.currency || 'CAD'}</p>
            <p className="text-xs text-gray-400">Total: {transaction.total_amount || transaction.price}$</p>
          </div>
          <div>
            <span className="text-gray-500 text-xs uppercase tracking-wide">Billets</span>
            <p className="font-medium">{transaction.ticket_count || transaction.num_tickets || 1}</p>
          </div>
        </div>

        {/* Ticket Details Section */}
        {(transaction.ticket_details?.length > 0 || transaction.section || transaction.row || transaction.seats || transaction.level || transaction.seat_type) && (
          <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
            <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <TicketCheck className="w-4 h-4 text-green-600" />
              Détails des billets
            </p>
            {transaction.seat_type && (
              <p className="text-xs text-gray-500 mb-2">
                Type: {transaction.seat_type === 'siege_assigne' ? 'Siège assigné' : 
                       transaction.seat_type === 'admission_generale' ? 'Admission générale' : 
                       transaction.seat_type}
              </p>
            )}
            {transaction.ticket_details && transaction.ticket_details.length > 0 ? (
              <div className="space-y-1">
                {transaction.ticket_details.map((ticket, idx) => (
                  <p key={idx} className="text-sm text-gray-700 bg-gray-50 rounded px-2 py-1">
                    {[
                      ticket.level && `Niveau ${ticket.level}`,
                      ticket.section && `Section ${ticket.section}`,
                      ticket.row && `Rangée ${ticket.row}`,
                      ticket.seat && `Siège ${ticket.seat}`
                    ].filter(Boolean).join(' / ')}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-700">
                {[
                  transaction.level && `Niveau ${transaction.level}`,
                  transaction.section && `Section ${transaction.section}`,
                  transaction.row && `Rangée ${transaction.row}`,
                  transaction.seats && `Siège ${transaction.seats}`
                ].filter(Boolean).join(' / ') || 'Aucun détail de siège'}
              </p>
            )}
            {transaction.special_section_details && (
              <p className="text-xs text-gray-500 mt-1">
                Note: {transaction.special_section_details}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="relative space-y-0">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Ligne de temps
        </h4>
        
        {timelineSteps.map((step, index) => {
          const isCompleted = step.isCompleted();
          const Icon = step.icon;
          const isLast = index === timelineSteps.length - 1;
          const completionInfo = getStepCompletionInfo(step.key);
          const availableActions = step.actions.filter(a => 
            (a.showWhen === 'completed' && isCompleted) || 
            (a.showWhen === 'not_completed' && !isCompleted)
          );

          return (
            <div key={step.key} className="relative flex">
              {/* Vertical Line */}
              {!isLast && (
                <div 
                  className="absolute left-6 top-12 w-0.5 h-full -ml-px"
                  style={{ backgroundColor: isCompleted ? '#10b981' : '#e5e7eb' }}
                />
              )}

              {/* Circle with Icon */}
              <div className="relative z-10 flex-shrink-0">
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center
                  transition-all duration-300
                  ${isCompleted 
                    ? 'bg-green-500 text-white shadow-lg'
                    : 'bg-white border-2 border-gray-300 text-gray-400'
                  }
                `}>
                  <Icon className="w-5 h-5" />
                </div>
              </div>

              {/* Content */}
              <div className="ml-4 flex-1 pb-6">
                <div className={`p-4 rounded-lg border ${
                  isCompleted 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-white border-gray-200'
                }`}>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-start justify-between flex-wrap gap-2">
                      <div className="flex-1">
                        <p className={`font-semibold ${isCompleted ? 'text-green-800' : 'text-gray-700'}`}>
                          {step.label}
                        </p>
                        <p className={`text-sm ${isCompleted ? 'text-green-600' : 'text-gray-500'}`}>
                          {step.description}
                        </p>
                      </div>
                      
                      {/* Status */}
                      <div className="flex items-center gap-1">
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {/* Completion info - Date/Time and By whom */}
                    {completionInfo && (
                      <div className="text-xs text-gray-500 bg-gray-50 rounded p-2 mt-1">
                        {completionInfo.date && (
                          <span className="mr-3">📅 {completionInfo.date}</span>
                        )}
                        {completionInfo.by && (
                          <span>👤 {completionInfo.by}</span>
                        )}
                      </div>
                    )}

                    {/* Action buttons */}
                    {(availableActions.length > 0 || (step.smsReminder && !isCompleted) || (step.rejectAction && !isCompleted && !transaction.cancelled)) && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {availableActions.map((actionDef) => (
                          <Button
                            key={actionDef.action}
                            size="sm"
                            className={getButtonVariant(actionDef.variant)}
                            onClick={() => handleAction(actionDef.action)}
                            disabled={loading[actionDef.action]}
                          >
                            {loading[actionDef.action] ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                En cours...
                              </>
                            ) : (
                              <>
                                {actionDef.variant === 'destructive' && <RotateCcw className="w-4 h-4 mr-1" />}
                                {actionDef.label}
                              </>
                            )}
                          </Button>
                        ))}
                        {step.smsReminder && !isCompleted && (
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={step.smsReminder.label || 'Envoyer un rappel SMS'}
                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                            onClick={() =>
                              openSmsReminderModal(
                                step.smsReminder.party,
                                step.smsReminder.step,
                                step.smsReminder.label
                              )
                            }
                            data-testid={`sms-reminder-${step.key}`}
                          >
                            <Bell className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Rappel SMS</span>
                            <span className="sm:hidden">SMS</span>
                          </Button>
                        )}
                        {step.rejectAction && !isCompleted && !transaction.cancelled && (
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={step.rejectAction.label}
                            className="border-red-300 text-red-700 hover:bg-red-50"
                            onClick={() => openCancelModal(step.rejectAction.prefillReason)}
                            data-testid={`reject-${step.key}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Rejeter — {step.rejectAction.label}</span>
                            <span className="sm:hidden">Rejeter</span>
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Admin Notes Section */}
      <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
        <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Notes administrateur (visible uniquement par les admins)
        </h4>
        <Textarea
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
          placeholder="Ajouter des notes internes sur cette transaction..."
          className="min-h-[100px] bg-white"
        />
        <div className="mt-3 flex justify-end">
          <Button
            onClick={handleSaveNotes}
            disabled={savingNotes}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {savingNotes ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Enregistrer les notes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Transaction History */}
      <div className="border rounded-lg overflow-hidden">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full p-4 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
        >
          <span className="font-semibold text-gray-700 flex items-center gap-2">
            <History className="w-5 h-5" />
            Historique des actions ({history.length})
          </span>
          {showHistory ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {showHistory && (
          <div className="p-4 max-h-64 overflow-y-auto">
            {history.length === 0 ? (
              <p className="text-gray-500 text-sm">Aucun historique disponible</p>
            ) : (
              <div className="space-y-3">
                {[...history].reverse().map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-sm border-b pb-2 last:border-0">
                    <div className="w-2 h-2 rounded-full bg-brand-500 mt-2 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{entry.description || entry.action}</p>
                      <div className="text-xs text-gray-500 mt-1">
                        {entry.timestamp && <span className="mr-3">📅 {formatDate(entry.timestamp)}</span>}
                        {entry.admin_email && <span>👤 {entry.admin_email}</span>}
                        {entry.user_email && <span>👤 {entry.user_email}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Warning for disputed/expired transactions */}
      {(transaction.status === 'disputed' || transaction.status === 'expired') && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800">
              {transaction.status === 'disputed' ? 'Transaction en litige' : 'Transaction expirée'}
            </p>
            <p className="text-sm text-red-600 mt-1">
              {transaction.status === 'disputed' 
                ? 'Cette transaction nécessite une intervention manuelle.' 
                : 'Cette transaction a expiré. Vous pouvez toujours modifier les étapes si nécessaire.'}
            </p>
          </div>
        </div>
      )}

      {/* Cancel Transfer Modal */}
      <Dialog open={cancelModal} onOpenChange={(open) => { if (!open) closeCancelModal(); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cancel-transfer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg text-red-700">
              <XCircle className="w-5 h-5" />
              Annuler le transfert
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              La transaction sera marquée comme annulée. Un SMS sera envoyé à l&apos;acheteur ET au vendeur. Si l&apos;un d&apos;eux avait déjà payé ou envoyé ses billets, les étapes de remboursement/retour apparaîtront ci-dessous.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-gray-700">
              Raison de l&apos;annulation *
            </label>
            <select
              value={cancelReasonPreset}
              onChange={(e) => handlePresetChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid="cancel-reason-preset"
              disabled={cancelling}
            >
              <option value="">-- Choisir une raison --</option>
              {CANCEL_REASON_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            {(cancelReasonPreset === 'autre' || cancelReasonPreset === '') && (
              <>
                <label className="block text-xs font-semibold text-gray-700 pt-1">
                  {cancelReasonPreset === 'autre' ? 'Précisez la raison *' : 'Raison libre *'}
                </label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value.slice(0, 500))}
                  placeholder="Ex: Délai de 12h dépassé sans confirmation du vendeur. / Annulation demandée par l'acheteur."
                  className="min-h-[110px] text-sm"
                  data-testid="cancel-reason-textarea"
                />
                <p className="text-[11px] text-gray-500">{cancelReason.length}/500 caractères — sera visible dans les SMS et les logs.</p>
              </>
            )}
            {cancelReasonPreset && cancelReasonPreset !== 'autre' && (
              <p className="text-[11px] text-gray-600 italic">
                Raison sélectionnée : <span className="font-medium">{cancelReason}</span>
              </p>
            )}

            {(transaction.payment_completed || transaction.tickets_verified) && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs sm:text-sm text-amber-900">
                <strong>À l&apos;annulation :</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {transaction.payment_completed && <li>Un remboursement Interac sera dû à l&apos;acheteur.</li>}
                  {transaction.tickets_verified && <li>Les billets devront être retournés au vendeur.</li>}
                </ul>
              </div>
            )}

            {/* Preuve visuelle (evidence upload) */}
            <div className="mt-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Preuves visuelles <span className="font-normal text-gray-500">(recommandé pour montant/billets non conformes)</span>
              </label>
              <p className="text-[11px] text-gray-500 mb-2">
                JPEG/PNG/WEBP/GIF — max 8 MB par image. Visible uniquement pour les administrateurs.
              </p>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                multiple
                onChange={handleEvidenceUpload}
                disabled={cancelling}
                className="block w-full text-xs sm:text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-brand-100 file:text-brand-700 hover:file:bg-brand-200 cursor-pointer"
                data-testid="cancel-evidence-input"
              />
              {evidenceFiles.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {evidenceFiles.map((f) => (
                    <li
                      key={f.tempId}
                      className="flex items-center justify-between gap-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs"
                      data-testid={`evidence-item-${f.tempId}`}
                    >
                      <span className="truncate flex-1 min-w-0 flex items-center gap-2">
                        {f.uploading ? (
                          <Loader2 className="w-3 h-3 animate-spin text-brand-600 flex-shrink-0" />
                        ) : (
                          <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />
                        )}
                        <span className="truncate">{f.filename}</span>
                      </span>
                      {!f.uploading && (
                        <button
                          type="button"
                          onClick={() => removeEvidence(f.tempId)}
                          className="text-red-600 hover:text-red-800 flex-shrink-0"
                          aria-label="Supprimer la preuve"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={closeCancelModal}
              disabled={cancelling}
              className="w-full sm:w-auto"
            >
              Retour
            </Button>
            <Button
              onClick={submitCancel}
              disabled={cancelling || cancelReason.trim().length < 3 || uploadingCount > 0}
              className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto"
              data-testid="cancel-confirm-btn"
            >
              {cancelling ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Annulation...</>
              ) : (
                <><XCircle className="w-4 h-4 mr-1" />Confirmer l&apos;annulation</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SMS Reminder Modal */}
      <Dialog open={smsModal.open} onOpenChange={(open) => { if (!open) closeSmsModal(); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sms-reminder-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Bell className="w-5 h-5 text-blue-600" />
              {smsModal.stepLabel || 'Envoyer un rappel SMS'}
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Modifiez le message si nécessaire avant de l&apos;envoyer par SMS.
            </DialogDescription>
          </DialogHeader>

          {smsTemplateLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-brand-600" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1 border border-gray-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Destinataire:</span>
                  <span className="font-semibold capitalize">{smsModal.party}</span>
                </div>
                {smsRecipientEmail && (
                  <p className="text-xs text-gray-600 break-all">{smsRecipientEmail}</p>
                )}
                {smsPhone ? (
                  <p className="text-xs text-blue-700 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {smsPhone}
                  </p>
                ) : (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Aucun numéro de téléphone enregistré
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Message SMS ({smsMessage.length}/480)
                </label>
                <Textarea
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value.slice(0, 480))}
                  className="min-h-[130px] text-sm"
                  placeholder="Le contenu du message SMS..."
                  data-testid="sms-reminder-textarea"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Le SMS sera envoyé au destinataire et l&apos;action sera consignée dans l&apos;historique.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={closeSmsModal}
              disabled={smsSending}
              className="w-full sm:w-auto"
            >
              Annuler
            </Button>
            <Button
              onClick={sendSmsReminder}
              disabled={smsSending || !smsMessage.trim() || !smsPhone || smsTemplateLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
              data-testid="sms-reminder-send-btn"
            >
              {smsSending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-1" />
                  Envoyer le rappel
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTransactionTimeline;
